import {
  Overlay,
  OverlayContainer,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NMultiselectOption = Record<string, any>;

@Component({
  selector: 'lib-n-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './n-multiselect.component.html',
  styleUrl: './n-multiselect.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NMultiselectComponent),
      multi: true,
    },
  ],
})
export class NMultiselectComponent implements ControlValueAccessor, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private overlay = inject(Overlay);
  private overlayContainer = inject(OverlayContainer);
  private vcr = inject(ViewContainerRef);

  @ViewChild('overlayPortal') overlayPortal!: TemplateRef<unknown>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() options: any[] = [];
  @Input() optionLabel = 'label';
  /** Propriedade usada como valor. Se vazio, usa o objeto completo como valor (padrão PrimeNG). */
  @Input() optionValue = '';
  @Input() placeholder = 'Selecione...';
  @Input() showHeader = true;
  @Input() maxSelectedLabels = 3;
  @Input() selectedItemsLabel = '{0} selecionados';
  @Input() display: 'chip' | 'comma' = 'chip';
  /** Quando false, emite um único valor (não array) e fecha o painel ao selecionar. */
  @Input() multiple = true;
  @Input() set disabled(v: boolean) {
    this._disabled = v;
    this.cdr.markForCheck();
  }

  /** Emitido ao selecionar/desselecionar uma opção. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<{ value: any }>();
  @ContentChild(TemplateRef) itemTemplate?: TemplateRef<any>;

  _isOpen = signal(false);
  _isClosing = signal(false);
  _searchTerm = signal('');
  _selectedValues: unknown[] = [];
  _disabled = false;

  private overlayRef: OverlayRef | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onChange: (v: any) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  // ─── CVA ─────────────────────────────────────────────────────────────────

  writeValue(value: unknown[] | unknown): void {
    if (this.multiple) {
      this._selectedValues = Array.isArray(value) ? value : [];
    } else {
      this._selectedValues = this.isEmptyValue(value) ? [] : [value];
    }
    this.cdr.markForCheck();
  }

  private isEmptyValue(value: unknown): boolean {
    return value == null || value === '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: (v: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this._disabled = disabled;
    this.cdr.markForCheck();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  getOptionLabel(option: NMultiselectOption): string {
    if (typeof option === 'string' || typeof option === 'number')
      return String(option);
    return String(option[this.optionLabel] ?? '');
  }

  getOptionValue(option: NMultiselectOption): unknown {
    if (typeof option === 'string' || typeof option === 'number') return option;
    return this.optionValue ? option[this.optionValue] : option;
  }

  isSelected(option: NMultiselectOption): boolean {
    const val = this.getOptionValue(option);
    if (typeof val === 'object' && val !== null) {
      return this._selectedValues.some((v) => v === val);
    }
    return this._selectedValues.includes(val);
  }

  get filteredOptions(): NMultiselectOption[] {
    const term = this._searchTerm().toLowerCase();
    if (!term) return this.options;
    return this.options.filter((o) =>
      this.getOptionLabel(o).toLowerCase().includes(term)
    );
  }

  get selectedOptions(): NMultiselectOption[] {
    return this.options.filter((o) => this.isSelected(o));
  }

  get visibleChips(): NMultiselectOption[] {
    return this.selectedOptions.slice(0, this.maxSelectedLabels);
  }

  get hiddenCount(): number {
    return Math.max(0, this._selectedValues.length - this.maxSelectedLabels);
  }

  get summaryLabel(): string {
    return this.selectedItemsLabel.replace(
      '{0}',
      String(this._selectedValues.length)
    );
  }

  get singleLabel(): string {
    if (this._selectedValues.length === 0) return '';
    const selected = this.options.find(
      (o) => this.getOptionValue(o) === this._selectedValues[0]
    );
    return selected ? this.getOptionLabel(selected) : '';
  }

  get showPlaceholder(): boolean {
    if (this._selectedValues.length === 0) return true;
    return !this.multiple && !this.singleLabel;
  }

  // ─── Panel ────────────────────────────────────────────────────────────────

  openPanel(): void {
    if (this._disabled || this._isOpen()) return;
    this._isOpen.set(true);
    this._isClosing.set(false);
    this._searchTerm.set('');
    this._onTouched();
    this.elevarOverlayContainer();
    this.attachOverlay();
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this._isOpen()) return;
    this._isClosing.set(true);
    this.cdr.markForCheck();
    setTimeout(() => {
      this._isOpen.set(false);
      this._isClosing.set(false);
      this.detachOverlay();
      this.restaurarOverlayContainer();
      this.cdr.markForCheck();
    }, 220);
  }

  ngOnDestroy(): void {
    this.restaurarOverlayContainer();
    this.disposeOverlay();
  }

  /**
   * Eleva o CDK overlay container acima de qualquer lib-bottom-sheet aberto (z-index 1050+).
   * Sem isso, o painel de seleção aparece atrás do bottom sheet.
   */
  private elevarOverlayContainer(): void {
    this.overlayContainer.getContainerElement().style.zIndex = '2000';
  }

  private restaurarOverlayContainer(): void {
    this.overlayContainer.getContainerElement().style.zIndex = '';
  }

  private attachOverlay(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.block(),
        positionStrategy: this.overlay
          .position()
          .global()
          .left('0')
          .top('0'),
        width: '100vw',
        height: '100dvh',
        panelClass: 'nm-cdk-overlay-pane',
      });
    }

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(
        new TemplatePortal(this.overlayPortal, this.vcr)
      );
    }
  }

  private detachOverlay(): void {
    this.overlayRef?.detach();
  }

  private disposeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  toggleOption(option: NMultiselectOption): void {
    const val = this.getOptionValue(option);
    if (this.multiple) {
      const current = [...this._selectedValues];
      const idx = current.indexOf(val);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(val);
      }
      this._selectedValues = current;
      this._onChange(current);
      this.selectionChange.emit({ value: current });
    } else {
      this._selectedValues = [val];
      this._onChange(val);
      this.selectionChange.emit({ value: val });
      this.close();
    }
    this.cdr.markForCheck();
  }

  removeChip(value: unknown, event: Event): void {
    event.stopPropagation();
    this._selectedValues = this._selectedValues.filter((v) => v !== value);
    this._onChange([...this._selectedValues]);
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this._isOpen()) this.close();
  }
}
